import { MERCHANT_TERMS } from '@field-support/shared';

/**
 * Form builder copy (Arabic). `forms`, `preview` and `formIssues` are carried
 * verbatim from the previous app; `forms.definition`, `forms.problems.pattern*`
 * and the dialog / version keys are new: the previous app showed the shared
 * validator's English messages as they were, this one translates them by shape.
 */
export const formsAr = {
  forms: {
    title: 'مصمم النماذج',
    description: 'نموذج الزيارة الذي يملؤه المندوب، وربط كل حقل بتذكرة Zoho Desk.',
    statusUnsaved: 'المسودة بها تغييرات غير محفوظة',
    statusSaved: 'المسودة محفوظة — لم تُنشر في هذه الجلسة',
    statusPublished: 'تم نشر المسودة',
    statusBlocked: 'النشر متوقف — عالج المشاكل المذكورة',
    draft: 'المسودة',
    fields: 'الحقول',
    formName: 'اسم النموذج',
    subjectTemplate: 'قالب عنوان التذكرة',
    subjectTemplateHint: 'المتغيرات المتاحة (ومفاتيح الحقول أيضاً):',
    addField: 'إضافة حقل',
    saveDraft: 'حفظ المسودة',
    publish: 'نشر إصدار جديد',
    publishConfirm: 'تصبح المسودة المحفوظة إصدارًا جديدًا يستلمه المندوبون عند المزامنة، والزيارات على الإصدارات القديمة ستظل مقبولة.',
    published: 'تم نشر الإصدار {{version}}',
    // New copy (UX audit #2): needs native review.
    publishTitle: 'نشر إصدار جديد؟',
    copyOverUnsavedTitle: 'تجاهل التغييرات غير المحفوظة؟',
    copyOverUnsavedConsequence: 'تحميل هذا الإصدار يستبدل المسودة، فتضيع تغييراتك غير المحفوظة.',
    copyOverUnsavedConfirm: 'تجاهل وتحميل',
    basedOn: 'مبنية على الإصدار {{version}}',
    issues: 'مشاكل يجب حلها قبل النشر',
    warnings: 'تنبيهات',
    versions: 'الإصدارات المنشورة',
    activeVersion: 'النشط',
    loadIntoDraft: 'نسخ للمسودة',
    fieldKey: 'المفتاح',
    label: 'العنوان',
    labelAr: 'العنوان (عربي)',
    labelEn: 'العنوان (إنجليزي)',
    labelEnHint: 'اختياري. يظهر لما التطبيق يكون بالإنجليزي؛ غير كده يظهر العنوان العربي.',
    newFieldLabel: 'حقل جديد {{n}}',
    newOptionLabel: 'اختيار {{n}}',
    problems: {
      cannotSave: 'المسودة ما اتحفظتش. صلّح دول الأول:',
      cannotPublish: 'المسودة لسه مينفعش تتنشر. شوف المشاكل تحت.',
      needsFix: 'محتاج تصليح',
      label: 'العنوان العربي مطلوب',
      labelEn: 'العنوان الإنجليزي طويل جداً',
      fieldKey: 'المفتاح لازم يكون حروف إنجليزي صغيرة وأرقام و _ ويبدأ بحرف',
      option: 'الاختيار {{n}} محتاج عنوان وقيمة',
      captureHint: 'إرشاد التصوير طويل جداً (200 حرف كحد أقصى)',
      formName: 'اسم النموذج مطلوب',
      fieldNumber: 'الحقل {{n}}',
      beforeSave: 'المسودة مش هتتحفظ قبل ما تصلّح دول:',
      patternInvalid: 'نمط الـ Regex مش تعبير صحيح',
      patternNested: 'نمط الـ Regex بيكرر مجموعة متكررة (زي ‎(a+)*‎)، وده ممكن يعلّق التحقق؛ بسّطه',
      rules: 'قاعدة من قواعد التحقق برا المسموح',
    },
    definition: {
      duplicateKey: 'المفتاح "{{key}}" متكرر',
      noOptions: 'حقل الاختيار محتاج اختيار واحد على الأقل',
      duplicateOptions: 'قيم الاختيارات لازم تكون مختلفة',
      minMaxLength: 'أقل طول لازم ميزيدش عن أقصى طول',
      minMax: 'أقل قيمة لازم متزيدش عن أقصى قيمة',
      minMaxItems: 'أقل عدد لازم ميزيدش عن أقصى عدد',
      photoNoZoho: 'الصور بتتبعت مرفقات على التذكرة ومينفعش تتربط بحقل في التذكرة',
      zohoNameWithoutTarget: 'امسح اسم حقل Zoho أو اختار هيتبعت فين',
      zohoNameRequired: 'اختار حقل Zoho اللي هيتبعت له',
      zohoCustomPrefix: 'أسماء الحقول المخصصة في Zoho بتبدأ بـ "cf_"',
      zohoSystemAttribute: 'اختار خاصية من خصائص التذكرة اللي في القائمة',
      zohoDuplicate: 'حقل Zoho "{{name}}" مربوط بالفعل بـ "{{other}}"',
      selfDependency: 'الحقل مينفعش يعتمد على نفسه',
      conditionEarlier: 'الشرط بيعتمد على "{{key}}"، ولازم يكون حقل قبله',
      conditionNoValue: `الشروط بتعتمد بس على حقول ليها قيمة (مش الصور ولا ${MERCHANT_TERMS.ar.the} ولا الفرع)`,
      conditionValue: 'اختار قيمة للشرط ده',
      conditionList: 'IN محتاج قائمة قيم',
      branchOnProject: 'حقل مرة واحدة للجولة مينفعش يعتمد على حقل لكل مشروع',
      merchantScope: `${MERCHANT_TERMS.ar.the} والفرع بيتسألوا مرة واحدة للجولة؛ مينفعش يكونوا لكل مشروع`,
      roleScope: 'دور التقارير لحقول لكل مشروع بس',
      roleType: 'دور «{{role}}» محتاج حقل من نوع تاني',
      roleUsed: 'دور «{{role}}» مستخدم بالفعل في "{{other}}"',
      toggleType: 'حقول نعم / لا بس اللي ممكن تظهر كمفتاح',
      dependsType: 'حقول الاختيار بس اللي ممكن تعتمد على حقل تاني',
      dependsEarlier: 'اختياراته بتعتمد على "{{key}}"، ولازم يكون حقل قبله',
      dependsParentType: 'الحقل الأب لازم يكون اختيار واحد',
      dependsOption: '"{{value}}" مبقاش من الاختيارات',
      dependsEmpty: 'إجباري، بس مفيش اختيارات لـ: {{options}}',
      prefillType: 'حقول الاختيار الواحد بس اللي ممكن تتملي تلقائياً',
      prefillSelf: 'الحقل مينفعش يتملي تلقائياً من نفسه',
      prefillEarlier: 'بيتملي تلقائياً من "{{key}}"، ولازم يكون حقل قبله',
      prefillSourceType: 'الملء التلقائي محتاج حقل مصدر اختيار واحد',
      prefillNoMatch: 'مفيش اختيار في "{{key}}" له نفس مفتاح اختيار في الحقل ده، فمش هيتملي حاجة',
      projectRoleMissing: 'حقول لكل مشروع محتاجة حقل مشروع واحد (اختيار واحد، دور المشروع) يسمّي كل بلوك',
      oneMerchant: `مسموح بحقل ${MERCHANT_TERMS.ar.one} واحد بس`,
      oneBranch: 'مسموح بحقل فرع واحد بس',
      branchNeedsMerchant: `حقل الفرع محتاج حقل ${MERCHANT_TERMS.ar.one}`,
      merchantConditional: `حقول ${MERCHANT_TERMS.ar.the} والفرع مينفعش يكون ليها شروط`,
      noFields: 'النموذج مفيهوش حقول',
      zohoNotLoaded: 'حقول تذاكر Zoho متحمّلتش، فأسماء الحقول المربوطة متراجعتش على Zoho',
      zohoValueType: 'نوع الحقل ده مينفعش يتبعت لـ Zoho كـ {{valueType}}',
      branchMapping: 'حقل الفرع لازم يتربط بحقل في Zoho (محتاجينه لتصحيح الفروع على التذاكر)',
      notMapped: 'مش بيتبعت لـ Zoho؛ قيمته بتتحفظ في سجل الزيارة بس',
      zohoMissing: 'مفيش حقل تذكرة في Zoho اسمه "{{name}}"',
      zohoIncompatible: 'نوع حقل Zoho "{{zohoType}}" مبيقبلش قيم {{valueType}}',
      zohoPicklist: 'قائمة Zoho بتقبل قيمها المسموحة بس؛ استخدم حقل اختيار واحد باختيارات مطابقة',
      zohoMultiselect: 'اختيار Zoho المتعدد مستني قيم من قائمته المسموحة',
      zohoText: 'القيمة هتتبعت لحقل نص',
      zohoSingleValue:
        'حقل Zoho "{{name}}" بياخد قيمة واحدة؛ في جولة فيها كذا مشروع بياخد قيمة الزيارة الأساسية والباقي بيتكتب في وصف التذكرة',
      zohoMandatory: 'Zoho معتبر "{{label}}" ({{name}}) إجباري ومفيش حقل في النموذج مربوط بيه',
      unknown: 'راجع إعدادات الحقل ده',
      unknownForm: 'راجع إعدادات النموذج',
    },
    editField: 'تعديل الحقل',
    applyField: 'تطبيق على المسودة',
    viewVersion: 'عرض',
    versionTitle: 'الإصدار {{version}}',
    type: 'النوع',
    required: 'إجباري',
    placeholder: 'نص توضيحي',
    helpText: 'مساعدة',
    options: 'الاختيارات',
    optionLabel: 'نص الاختيار',
    optionValue: 'القيمة',
    addOption: 'إضافة اختيار',
    removeOption: 'حذف الاختيار',
    delimiter: 'الفاصل',
    characters: 'الحروف المسموحة',
    rules: 'التحقق',
    minLength: 'أقل طول',
    maxLength: 'أقصى طول',
    min: 'أقل قيمة',
    max: 'أقصى قيمة',
    maxItems: 'أقصى عدد',
    pattern: 'نمط Regex',
    errorMessage: 'رسالة الخطأ',
    photoQuality: 'جودة الصورة',
    photoQualityLevel: 'مستوى الجودة',
    photoQualityLevels: { STANDARD: 'عادية', HIGH: 'عالية' },
    photoExpectedSize: 'الحجم المتوقع',
    photoQualityAdvice:
      'استخدم «عالية» فقط حيث يجب أن يكون النص الصغير مقروءاً — أرقام السيريال، الفواتير، لوحات البيانات. كل صورة عالية حجمها حوالي الضعف، وده يكلّف الخادم باندويدث والمندوب باقة النت.',
    captureHint: 'إرشاد التصوير',
    captureHintHelp: 'يظهر للمندوب فوق زر الكاميرا. طريقة التصوير أهم من الدقة لوضوح النص.',
    visibleWhen: 'يظهر فقط عندما',
    addCondition: 'إضافة شرط',
    removeCondition: 'حذف الشرط',
    zohoTarget: 'يُرسل إلى Zoho كـ',
    zohoField: 'حقل Zoho',
    zohoValueType: 'نوع القيمة',
    zohoFieldsUnavailable: 'تعذر تحميل حقول Zoho؛ اكتب اسم الحقل.',
    loadZohoFields: 'تحميل حقول Zoho',
    moveUp: 'لأعلى',
    moveDown: 'لأسفل',
    removeField: 'حذف الحقل',
    preview: 'معاينة الموبايل',
    previewNote: 'معاينة فقط — لا يتم إرسال أي شيء.',
    systemMappings: 'حقول الخلفية (الموقع والوقت والمندوب)',
    saveMappings: 'حفظ الربط',
    version: 'الإصدار',
    publishedAt: 'تاريخ النشر',
    publishedBy: 'بواسطة',
    fieldsVisits: 'الحقول / الزيارات',
    fieldTypes: {
      TEXT: 'نص',
      TEXTAREA: 'نص طويل',
      NUMBER: 'رقم',
      CURRENCY: 'مبلغ',
      PHONE: 'رقم موبايل',
      SELECT: 'اختيار واحد',
      MULTI_SELECT: 'اختيارات متعددة',
      BOOLEAN: 'نعم / لا',
      ATTACHMENT_IMAGE: 'صور',
      BRAND_SELECT: `${MERCHANT_TERMS.ar.the}`,
      BRANCH_SELECT: 'الفرع',
    },
    targets: { NONE: 'لا يُرسل', CUSTOM_FIELD: 'حقل مخصص', SYSTEM_FIELD: 'خاصية التذكرة' },
    scope: 'يُسأل',
    scopeHint: 'لكل مشروع: يُسأل مرة أخرى في كل زيارة مشروع إضافية. مرة واحدة للجولة: التاجر والفرع والمدير.',
    scopes: { VISIT: 'مرة واحدة للجولة (الفرع)', PROJECT: 'لكل مشروع' },
    role: 'دور الحقل في التقارير',
    roleHint: 'يعرّف جدول الزيارات والفلاتر وحقول Zoho المجمّعة بمعنى هذا الحقل. كل دور مرة واحدة.',
    roles: {
      none: 'بدون',
      PROJECT: 'المشروع (اسم البلوك)',
      VISIT_TYPE: 'نوع الزيارة',
      SERVICE: 'الخدمة',
      SERVICE_DETAIL: 'تفاصيل الخدمة',
      ISSUE_FLAG: 'يوجد مشكلة',
      ISSUE_TYPE: 'نوع المشكلة',
      ISSUES: 'المشاكل',
      COMMENT: 'التعليق',
    },
    optionLabelEn: 'الاسم بالإنجليزية',
    optionKeysHint:
      'القيمة هي المفتاح الثابت الذي يُخزَّن ويُرسل إلى Zoho وتُبنى عليه التقارير. يمكن تغيير الأسماء لاحقاً؛ لا تغيّر المفتاح.',
    multiChoice: 'يسمح بأكثر من إجابة',
    dependsOn: 'الاختيارات تعتمد على',
    dependsOnParent: 'الحقل الأب',
    dependsOnNone: 'مستقل (كل الاختيارات)',
    dependsOnHint: 'لكل اختيار في الحقل الأب، علّم الاختيارات المتاحة. تغيير الأب على الموبايل يمسح هذه الإجابة.',
    dependentFor: 'عند اختيار «{{option}}»',
    prefillFrom: 'يُملأ تلقائياً من',
    prefillNone: 'بدون ملء تلقائي',
    prefillHint: 'يُملأ بالاختيار الذي له نفس المفتاح. يمكن للمندوب تغييره؛ القيمة المُغيَّرة تبقى ويظهر أنها مختلفة.',
    booleanStyle: 'يظهر كـ',
    booleanStyles: { CHOICE: 'اختيار نعم / لا', TOGGLE: 'مفتاح تشغيل، مغلق افتراضياً' },
    reveals: 'حقول تظهر عند تشغيل المفتاح',
    revealsHint: 'كل حقل مُعلَّم يأخذ شرط «هذا المفتاح مُشغَّل».',
  },
  preview: {
    selectBrand: `اختر ${MERCHANT_TERMS.ar.the}`,
    selectBranch: 'اختر الفرع',
    photosPlaceholder: 'يتم التقاط الصور بالكاميرا داخل التطبيق',
    addPhoto: 'إضافة صورة',
    valid: 'كل الحقول الإجبارية صحيحة',
    chooseFirst: 'اختر {{parent}} أولاً',
    overridden: 'مختلف عن مصدر الملء التلقائي ({{value}})؛ تم الإبقاء على الاختيار.',
    projectBlock: 'بلوك المشروع (يتكرر في كل زيارة إضافية)',
  },
  formIssues: {
    REQUIRED: 'إجباري',
    INVALID_OPTION: 'اختر من الاختيارات',
    INVALID_PHONE: 'اكتب رقم موبايل من 11 رقم يبدأ بـ 01',
    INVALID_CHARACTERS: 'فيه حروف أو رموز مش مسموحة',
    PATTERN_MISMATCH: 'مش بالشكل المطلوب',
    INVALID_TYPE: 'قيمة غير صحيحة',
    OPTION_NOT_FOR_PARENT: 'مش من اختيارات الاختيار اللي فوق',
    TOO_SHORT: 'أقصر من المسموح',
    TOO_LONG: 'أطول من المسموح',
    TOO_SMALL: 'أقل من المسموح',
    TOO_LARGE: 'أكبر من المسموح',
    TOO_FEW_ITEMS: 'أقل من العدد المطلوب',
    TOO_MANY_ITEMS: 'أكتر من المسموح',
    WRONG_LENGTH: 'الطول غلط',
    UNKNOWN_FIELD: 'حقل غير معروف',
    default: 'غير صحيح',
  },
};
